export type AppearanceSection = 'theme' | 'branding' | 'ui';

export type AppearanceCommitStatus =
  | 'idle'
  | 'running'
  | 'success'
  | 'partial'
  | 'failed'
  | 'blocked';

export interface AppearanceSectionCommitResult {
  section: AppearanceSection;
  status: 'success' | 'error';
  message?: string;
  errorCode?: string;
}

export interface AppearanceCommitResult {
  status: AppearanceCommitStatus;
  attempted: AppearanceSection[];
  succeeded: AppearanceSection[];
  failed: AppearanceSectionCommitResult[];
  /** Sections completed so far while status === 'running' */
  completedCount: number;
  /** Total sections in this commit attempt */
  totalCount: number;
}

export const EMPTY_APPEARANCE_COMMIT_RESULT: AppearanceCommitResult = {
  status: 'idle',
  attempted: [],
  succeeded: [],
  failed: [],
  completedCount: 0,
  totalCount: 0,
};

export const APPEARANCE_SECTION_ERROR_FALLBACK: Record<AppearanceSection, string> = {
  theme: 'Không thể lưu cấu hình màu sắc.',
  branding: 'Không thể lưu cấu hình thương hiệu.',
  ui: 'Không thể lưu cấu hình giao diện.',
};

/** Normalize thrown errors into a safe Vietnamese UI message (no stack/SQL/raw HTTP body). */
export function normalizeAppearanceCommitError(
  section: AppearanceSection,
  err: unknown,
): { message: string; errorCode?: string } {
  const fallback = APPEARANCE_SECTION_ERROR_FALLBACK[section];
  if (err == null) return { message: fallback };

  if (typeof err === 'object' && err !== null) {
    const maybe = err as { code?: string; message?: string; status?: number };
    const code = typeof maybe.code === 'string' ? maybe.code : undefined;
    const raw = typeof maybe.message === 'string' ? maybe.message.trim() : '';
    // Reject messages that look like raw transport / SQL / stack leaks.
    const unsafe =
      !raw ||
      /stack|sql|axios|ECONN|JWT|Bearer|at\s+\w+\s+\(/i.test(raw) ||
      raw.length > 180 ||
      /^HTTP\s+\d{3}/i.test(raw);
    if (!unsafe && raw.length > 0) {
      return { message: raw, errorCode: code };
    }
    return { message: fallback, errorCode: code };
  }

  if (typeof err === 'string') {
    const unsafe = /stack|sql|axios|ECONN|JWT|Bearer|^HTTP\s+\d{3}/i.test(err) || err.length > 180;
    return { message: unsafe || !err.trim() ? fallback : err.trim() };
  }

  return { message: fallback };
}
